import {Injectable, signal} from '@angular/core';
import {SearchBodyMap, SearchIndexName, SearchParamMap, SearchResponse} from '@models/search';
import {TileInfo} from '@models/tile-info';
import {SearchService} from '@services/search/search.service';
import {generateTileList} from '@utilities/tiles';

// SearchManager: Handles all search state changes.
// This is responsible for the search page state and state changes
// Manages communication between the UI (search-bar) and API (search.service)

export interface SearchExecutionConfig<K extends SearchIndexName = SearchIndexName> {
  body: SearchBodyMap[K];
  params: SearchParamMap[K];
  index: K;
  perPage: number;
  queryKey: string;
}

interface PageSearchResult {
  page: number;
  results: SearchResponse[];
  hasMore: boolean;
}

@Injectable()
export class SearchManagerService {
  readonly loading = signal(false);
  readonly results = signal<TileInfo[]>([]);
  readonly currentPage = signal(1);
  readonly hasMoreResults = signal(true);

  private loadingPromise: Promise<void> | null = null;
  private queryKey = '';
  private readonly MAX_EMPTY_PAGE_SCANS = 20;

  constructor(private readonly searchService: SearchService) {}

  async executeSearch(config: SearchExecutionConfig) {
    this.resetSearchState(config);
    await this.loadNextPage(config);
  }

  async loadNextPage(config: SearchExecutionConfig) {
    if (!this.canLoadNextPage()) {
      return;
    }

    if (this.loadingPromise !== null) {
      await this.loadingPromise;
      return;
    }

    this.loading.set(true);
    const localKey = this.queryKey;
    const pageLoadPromise = this.executePageLoad(config, localKey);

    this.loadingPromise = pageLoadPromise;
    await pageLoadPromise;
  }

  private async executePageLoad(config: SearchExecutionConfig, localKey: string) {
    try {
      const pageResult = await this.findNextValidPage(config, this.currentPage());

      // another search started while this one was running
      if (!this.isActiveQuery(localKey)) {
        return;
      }

      this.hasMoreResults.set(pageResult.hasMore);
      this.appendResults(pageResult.results);
      this.currentPage.set(pageResult.page + 1);
    } catch (err) {
      console.error('Search error while loading page', err);

      this.hasMoreResults.set(false);
    } finally {
      this.clearLoadingState();
    }
  }

  // findNextValidPage: Elasticsearch requires skipping past empty pages of results, to gather all results.
  private async findNextValidPage(config: SearchExecutionConfig, startingPage: number): Promise<PageSearchResult> {
    let page = startingPage;
    let results: SearchResponse[] = [];
    let hasMore = false;
    let emptyPageScans = 0;

    while (emptyPageScans < this.MAX_EMPTY_PAGE_SCANS) {
      const response = await this.searchService.searchGeneric(
        config.body,
        config.params,
        config.index,
        page,
        config.perPage,
      );

      results = response?.results ?? [];

      hasMore = response?.hasMore ?? false;

      // Found a usable page
      if (results.length > 0) {
        break;
      }

      // No additional pages available
      if (!hasMore) {
        break;
      }

      // Sparse/empty page — continue scanning
      page++;
      emptyPageScans++;
    }

    return {
      page,
      results,
      hasMore,
    };
  }

  private appendResults(results: SearchResponse[]) {
    const newTiles = generateTileList(results);

    const existingIds = new Set(this.results().map(t => t.id));

    const uniqueNewTiles = newTiles.filter(t => !existingIds.has(t.id));

    this.results.set([...this.results(), ...uniqueNewTiles]);
  }

  private resetSearchState(config: SearchExecutionConfig) {
    this.currentPage.set(1);
    this.results.set([]);
    this.hasMoreResults.set(true);
    this.queryKey = config.queryKey;
  }

  private clearLoadingState() {
    this.loadingPromise = null;
    this.loading.set(false);
  }

  private canLoadNextPage(): boolean {
    return this.hasMoreResults() && !this.loading();
  }

  private isActiveQuery(localKey: string): boolean {
    return localKey === this.queryKey;
  }
}
